// src/modules/finance/components/DetailCashboxTransactionModal.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { CashTransaction } from "types/cashTransactionTypes";
import { formatMoney } from "helpers/numberHelper";
import { formatToLocaleDate } from "utils/dateUtils";
import { getLabelPaymentMethod } from "utils/stringUtils";
import CustomModal from "components/Common/CustomModal";
import Badge from "components/Common/ui/Badge";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";

type CashTransactionWithRef = CashTransaction & {
  reference?: any;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  transaction: CashTransactionWithRef | null;
};

const DetailCashboxTransactionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  if (!transaction) return null;

  const { referenceType, reference, type, amount, createdAt } = transaction;
  const isIncome = type === "in";

  const renderFeePaymentDetails = () => {
    if (referenceType !== "FeePayment" || !reference) return null;

    const { fee, paymentMethod } = reference;
    const { feeType, student } = fee || {};
    const studentName = student?.user
      ? `${student.user.firstName || ""} ${student.user.lastName || ""}`.trim()
      : null;

    return (
      <div className="mb-3">
        <div className="p-0">
          <table className="table table-sm mb-0">
            <tbody>
              <DetailRow
                label={t("student")}
                value={<UserProfileHeader
                  photo={student?.user?.photo}
                  firstName={student?.user?.firstName || ""}
                  lastName={student?.user?.lastName || ""}
                  subtitle={student?.massarCode || ""}
                  to={all_routes.studentDetails.replace(":id", student?.id || "") + all_routes.studentDetailsTabs.fees}
                  canRedirect={ability.can("getAll", "Fee")}
                />}
              />
              <DetailRow
                label={t("type")}
                value={feeType?.name || t("common.not_available")}
              />
              <DetailRow
                label={t("amount")}
                value={`${formatMoney(Number(fee?.amount || 0))} MAD`}
              />
              <DetailRow
                label={t("due_date")}
                value={
                  fee?.dueDate
                    ? formatToLocaleDate(fee.dueDate, "L")
                    : t("common.not_available")
                }
              />
              <DetailRow
                label={t("payment_method")}
                value={
                  paymentMethod
                    ? getLabelPaymentMethod(paymentMethod.name)
                    : t("common.not_available")
                }
              />
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderExpenseDetails = () => {
    if (referenceType !== "Expense" || !reference) return null;

    const { supplier, description, receiptNumber, notes, expenseDate } =
      reference;

    return (
      <div className="mb-3">
        <div className="p-0">
          <table className="table table-sm mb-0">
            <tbody>
              <DetailRow
                label={t("supplier")}
                value={supplier?.name || t("common.not_available")}
              />
              <DetailRow
                label={t("expense_date")}
                value={
                  expenseDate
                    ? formatToLocaleDate(expenseDate, "L")
                    : t("common.not_available")
                }
              />
              <DetailRow
                label={t("description")}
                value={description || t("common.not_available")}
              />
              <DetailRow
                label={t("receipt_number")}
                value={receiptNumber || t("common.not_available")}
              />
              <DetailRow
                label={t("payment_method")}
                value={getLabelPaymentMethod("Espèces")}
              />
              {notes && (
                <DetailRow
                  label={t("notes")}
                  value={notes}
                />
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      title={t("details")}
      isOpen={isOpen}
      onClose={onClose}
    >
      {/* Résumé transaction */}
      <div className="mb-3">
        <div className="p-0">
          <table className="table table-sm mb-0">
            <tbody>
              <DetailRow
                label={t("cashboxTransactions.fields.direction")}
                value={
                  <Badge text={isIncome
                    ? t("cashboxTransactions.type.in")
                    : t("cashboxTransactions.type.out")} variant={isIncome ? "success" : "danger"} />

                }
              />
              <DetailRow
                label={t("amount")}
                value={`${formatMoney(Number(amount))}`}
              />
              <DetailRow
                label={t("cashboxTransactions.fields.date")}
                value={formatToLocaleDate(createdAt, "L LT")}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* Type d'opération (Fee / Expense) */}
      <div className="mb-3">
        <div className="p-0">
          <table className="table table-sm mb-0">
            <tbody>
              <DetailRow
                label={t("cashboxTransactions.fields.operation")}
                value={
                  referenceType === "FeePayment"
                    ? t("cashboxTransactions.operation.fee_payment")
                    : referenceType === "Expense"
                      ? t("cashboxTransactions.operation.expense")
                      : t("common.not_available")
                }
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* Détails liés */}
      {referenceType === "FeePayment" && renderFeePaymentDetails()}
      {referenceType === "Expense" && renderExpenseDetails()}
      {(!referenceType || !reference) && (
        <div>
          <div className="text-center text-muted py-3">
            {t("cashboxTransactions.details.no_details")}
          </div>
        </div>
      )}
    </CustomModal>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: string | React.ReactNode;
}> = ({ label, value }) => (
  <tr>
    <th className="text-muted small" style={{ width: "40%" }}>
      {label}
    </th>
    <td className="fw-medium text-dark">{value}</td>
  </tr>
);

export default DetailCashboxTransactionModal;